import React from 'react';
import { ERROR_KIND, TERMINAL_COPY } from '../../utils/errorTaxonomy';
import { useAutoRecover } from '../../hooks/useAutoRecover';
import styles from './DataFallback.module.css';

/**
 * What to show in a region whose data did not arrive.
 *
 * The point is that the user keeps the page they were on. When the orders list
 * cannot load, the header, tabs and filters all stay mounted and only the table
 * body becomes this — so the failure reads as one section catching up rather
 * than the product falling over, and navigating elsewhere still works.
 *
 * `variant` picks a density, not a size; see DataFallback.module.css for how the
 * component adapts to the hole it is filling.
 *
 *   inline — one line, for a KPI value slot or a toolbar
 *   card   — for one tile in a dashboard grid; fills its track
 *   table  — for the body of a list, in a real <table> or a div grid
 *   pane   — for a whole route body
 */

/** Deliberately mild: a small mark, not a red warning triangle. */
const ICON_FOR_KIND = {
  [ERROR_KIND.DEPLOYING]: '🛠️',
  [ERROR_KIND.BUSY]: '⏳',
  [ERROR_KIND.RATE_LIMITED]: '⏳',
  [ERROR_KIND.QUOTA]: '🌙',
  [ERROR_KIND.OFFLINE]: '📡',
  [ERROR_KIND.NOT_FOUND]: '🔍',
  [ERROR_KIND.FORBIDDEN]: '🔒',
  [ERROR_KIND.CONFLICT]: '⏱️',
  [ERROR_KIND.VALIDATION]: '✏️',
  [ERROR_KIND.UNKNOWN]: '☕',
};

function secondsLeft(retryState, now) {
  if (!retryState || !retryState.nextAttemptAt) return 0;
  return Math.max(0, Math.ceil((retryState.nextAttemptAt - now) / 1000));
}

/**
 * The live countdown between attempts.
 *
 * A wait with nothing moving is indistinguishable from a hang, which is the
 * complaint this whole change answers — so the attempt count and a draining bar
 * are always visible while a retry is pending.
 */
function RetryProgress({ retryState, onRetryNow }) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!retryState) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [retryState]);

  if (!retryState) return null;

  const remaining = secondsLeft(retryState, now);
  const total = Math.max(1, retryState.delayMs || 0);
  // Drains left to right as the wait elapses.
  const fraction = Math.max(0, Math.min(1, (retryState.nextAttemptAt - now) / total));

  return (
    <div className={styles.retryStatus}>
      <div className={styles.retryLine}>
        <span>
          {remaining > 0 ? `Trying again in ${remaining}s…` : 'Trying again…'}
        </span>
        {onRetryNow && (
          <button type="button" className={styles.linkBtn} onClick={onRetryNow}>
            Try now
          </button>
        )}
      </div>
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={retryState.maxAttempts}
        aria-valuenow={retryState.attempt}
        aria-label={`Retrying, attempt ${retryState.attempt} of ${retryState.maxAttempts}`}
      >
        <div className={styles.progressFill} style={{ transform: `scaleX(${fraction})` }} />
      </div>
      <span className={styles.retryCount}>
        Attempt {retryState.attempt} of {retryState.maxAttempts}
      </span>
    </div>
  );
}

/**
 * When the next automatic attempt lands.
 *
 * Deliberately replaces the retry button rather than sitting beside it: the
 * point is to remove the temptation to click, not to offer both. Someone who
 * can see the app is still working on it does not reach for refresh.
 */
function NextAttemptCountdown({ at, children }) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const secondsLeftToProbe = Math.max(0, Math.ceil((at - now) / 1000));

  return (
    <div className={styles.retryStatus}>
      <div className={styles.retryLine}>
        <span>
          {secondsLeftToProbe > 0
            ? `Checking again in ${formatCountdown(secondsLeftToProbe)}…`
            : 'Checking now…'}
        </span>
      </div>
      {children}
    </div>
  );
}

/** "45s", "1m 20s", "3m" — short enough to read at a glance. */
function formatCountdown(totalSeconds) {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}

function DataFallback({
  error,
  retryState = null,
  onRetry,
  variant = 'pane',
  colSpan,
  title,
  message,
  actionLabel = 'Try again',
  children,
  className = '',
  // Off by default, and deliberately so. When this was on for everything, each
  // successful probe called back into the host page's fetch; on Orders that set
  // isPageLoading(true) and the banner stayed mounted, so the table span
  // "Loading orders…" indefinitely while the count showed the rows had arrived.
  // A surface opts in only once its retry is known to be safe to call
  // repeatedly and to clear the state that keeps this mounted.
  autoRecover = false,
}) {
  const kind = (error && error.kind) || ERROR_KIND.UNKNOWN;
  const copy = TERMINAL_COPY[kind] || TERMINAL_COPY.unknown;

  const shownTitle = title || (error && error.title) || copy.title;
  const shownMessage = message || (error && error.message) || copy.message;
  const icon = ICON_FOR_KIND[kind] || ICON_FOR_KIND[ERROR_KIND.UNKNOWN];

  // A retry the user can see is in progress; the button would be a second,
  // competing affordance.
  const isRetrying = Boolean(retryState);
  const canRetry = Boolean(onRetry) && (error ? error.retryable !== false : true);

  // Keep trying in the background while this is on screen, so an outage longer
  // than the per-request backoff does not leave the page waiting for a click.
  // Only for failures a retry could actually fix: there is no point probing a
  // 404, and re-requesting an exhausted daily quota would never succeed.
  const shouldAutoRecover =
    autoRecover && canRetry && error ? error.retryable !== false : false;
  const { nextProbeAt } = useAutoRecover(shouldAutoRecover, () => {
    if (onRetry) onRetry();
  });

  if (process.env.NODE_ENV !== 'production' && variant === 'table' && colSpan === undefined) {
    // Inside a real <tbody> we must emit <tr><td colSpan>; a bare <div> there is
    // invalid markup and React will warn about the nesting.
    console.warn(
      '[DataFallback] variant="table" inside a <table> needs a colSpan so the row ' +
        'can span the header. Omit it only when the list is a div grid.'
    );
  }

  const body = (
    <div
      className={`${styles.root} ${styles[variant] || styles.pane} ${className}`.trim()}
      role="status"
      aria-live="polite"
    >
      <span className={styles.icon} aria-hidden="true">
        {icon}
      </span>
      <p className={styles.title}>{shownTitle}</p>
      <p className={styles.message}>{shownMessage}</p>

      {isRetrying ? (
        <RetryProgress retryState={retryState} onRetryNow={onRetry} />
      ) : nextProbeAt ? (
        /*
          A countdown rather than a button. "Try again" is the most tempting
          thing on a page that is not working, and during a real overload a few
          hundred people pressing it is precisely the surge the backoff exists to
          avoid — every click also bypasses the pacing. Saying when the next
          attempt lands is both lighter on the server and more reassuring: a
          visible timer is what stops someone hammering refresh.
        */
        <NextAttemptCountdown at={nextProbeAt}>{children}</NextAttemptCountdown>
      ) : (
        (canRetry || children) && (
          <div className={styles.actions}>
            {canRetry && (
              <button type="button" className={styles.retryBtn} onClick={onRetry}>
                {actionLabel}
              </button>
            )}
            {children}
          </div>
        )
      )}
    </div>
  );

  // A <div> is not a legal child of <tbody>, so when the host is a real table we
  // provide the row ourselves rather than making every caller remember to.
  if (variant === 'table' && colSpan !== undefined) {
    return (
      <tr>
        <td colSpan={colSpan} style={{ padding: 0 }}>
          {body}
        </td>
      </tr>
    );
  }

  return body;
}

/**
 * Shown above data that is still on screen while a refresh is failing.
 *
 * Keeping the table readable during an outage is the point — a user mid-task
 * should not lose what they were looking at because a refresh could not reach
 * the server. But the staleness has to be stated, or the figures look current.
 */
export function StaleDataBanner({
  since,
  retryState,
  onRetry,
  // See DataFallback: off unless the host page's retry is safe to call on a
  // timer without re-entering its own loading state.
  autoRecover = false,
  // Set when the surface has also locked its controls, so the notice can say so
  // — a greyed-out field with no explanation just looks broken.
  actionsPaused = false,
}) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Stale rows have the same problem as an empty fallback: the refresh that
  // failed does not try again on its own, so the figures quietly stop ageing
  // toward the truth. Probing here means the banner clears itself once the
  // server is back.
  const { nextProbeAt } = useAutoRecover(Boolean(autoRecover && onRetry), () => {
    if (onRetry) onRetry();
  });

  // An exact timestamp, not "4 minutes ago". Someone deciding whether a status
  // is safe to rely on needs to know when it was true, and a relative age makes
  // that a subtraction they have to do themselves.
  const asOf = since ? new Date(since) : null;
  const stamp = asOf
    ? asOf.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })
    : null;

  const secondsToProbe = nextProbeAt ? Math.max(0, Math.ceil((nextProbeAt - now) / 1000)) : 0;

  return (
    <div className={styles.staleBanner} role="status" aria-live="polite">
      <span className={styles.staleDot} aria-hidden="true" />
      <span>
        {stamp ? `Showing data as of ${stamp}` : 'Showing the last data we received'}
        {nextProbeAt
          ? secondsToProbe > 0
            ? ` · checking again in ${formatCountdown(secondsToProbe)}`
            : ' · checking now…'
          : ' · reconnecting…'}
        {actionsPaused && ' · changes are paused until we reconnect'}
      </span>
    </div>
  );
}

export default DataFallback;
